import { AdvancedTagMapping } from 'src/features/gateway-details/types';
import { CustomErrors, DATA_TYPES, ModalTableRowData } from '../types';
import { UseFormClearErrors, UseFormSetValue } from 'react-hook-form';
import { isEqual } from 'lodash';
import { validateMinusSign } from './validate-minus-sign';
import { TABLE_VALIDATION_UNIQUE_VALUES_ERROR_MESSAGE } from '../config';
import { validateValueChange } from 'src/utilities';

export const areTableValuesUnique = (
  value: string,
  index: number,
  values: AdvancedTagMapping,
  setValue: UseFormSetValue<AdvancedTagMapping>,
  customErrors: CustomErrors,
  setCustomErrors: React.Dispatch<React.SetStateAction<CustomErrors>>,
  clearCustomErrors: (name: string, specificErrorValue: string, clearEntireKey?: boolean) => void,
  clearErrors: UseFormClearErrors<AdvancedTagMapping>,
  rowData: ModalTableRowData[],
  tableValueErrors: number[][],
  setTableValueErrors: React.Dispatch<React.SetStateAction<number[][]>>,
  maxDecimal: number | null = null,
  newValues: { key: number; value: string }[] | null = null
) => {
  const validMaxDecimal = maxDecimal ?? +values.maxDecimal;

  const newValue = validateValueChange(
    value,
    true,
    values.dataType === DATA_TYPES.FLOAT ? validMaxDecimal : 0
  );

  if (newValue === null || newValue === undefined)
    return { value: true, nonUniqueValueIndices: [] };
  setValue(`value-${index}`, newValue);

  if (newValue !== '') {
    clearCustomErrors(`value-${index}`, TABLE_VALIDATION_UNIQUE_VALUES_ERROR_MESSAGE);
    clearErrors(`value-${index}`);
  }

  let areUnique: { value: boolean; nonUniqueValueIndices: number[] } = {
    value: true,
    nonUniqueValueIndices: [],
  };

  checkTableValueErrorState(
    newValue,
    index,
    values,
    tableValueErrors,
    setTableValueErrors,
    clearCustomErrors
  );

  if (newValues) {
    newValues.forEach((value) => {
      if (value.key !== index && value.value === newValue) {
        areUnique.value = false;
        areUnique.nonUniqueValueIndices.push(value.key);
      }
    });
  } else {
    rowData.forEach((row) => {
      if (row.index !== index && values[`value-${row.index}`] === newValue) {
        areUnique.value = false;
        areUnique.nonUniqueValueIndices.push(row.index);
      }
    });
  }

  if (!areUnique.value) {
    setTableValueErrors((prevValue: number[][]) => {
      let nonUniqueFields: number[] = [index];
      areUnique.nonUniqueValueIndices.forEach((nonUniqueValueIndex) => {
        nonUniqueFields.push(nonUniqueValueIndex);
      });

      let newValue: number[][] = [];
      prevValue.forEach((value) => {
        if (!value.every((item) => nonUniqueFields.includes(item))) {
          newValue.push(value);
        }
      });

      newValue.push(nonUniqueFields);

      return newValue;
    });
    if (newValue !== '') {
      let messagesNew: string[] = [];
      const messages = customErrors[`value-${index}`]?.message;
      if (messages && !messages.includes(TABLE_VALIDATION_UNIQUE_VALUES_ERROR_MESSAGE)) {
        messages.push(TABLE_VALIDATION_UNIQUE_VALUES_ERROR_MESSAGE);
      } else if (!messagesNew.includes(TABLE_VALIDATION_UNIQUE_VALUES_ERROR_MESSAGE)) {
        messagesNew.push(TABLE_VALIDATION_UNIQUE_VALUES_ERROR_MESSAGE);
      }

      setCustomErrors((prevValue) => ({
        ...prevValue,
        [`value-${index}`]: { message: messages ?? messagesNew },
      }));
    }

    areUnique.nonUniqueValueIndices.forEach((nonUniqueValueIndex) => {
      let messagesNew: string[] = [];
      const messages = customErrors[`value-${nonUniqueValueIndex}`]?.message;
      if (messages && !messages.includes(TABLE_VALIDATION_UNIQUE_VALUES_ERROR_MESSAGE)) {
        messages.push(TABLE_VALIDATION_UNIQUE_VALUES_ERROR_MESSAGE);
      } else if (!messagesNew.includes(TABLE_VALIDATION_UNIQUE_VALUES_ERROR_MESSAGE)) {
        messagesNew.push(TABLE_VALIDATION_UNIQUE_VALUES_ERROR_MESSAGE);
      }
      if (values[`value-${nonUniqueValueIndex}`] !== '') {
        setCustomErrors((prevValue) => ({
          ...prevValue,
          [`value-${nonUniqueValueIndex}`]: { message: messages ?? messagesNew },
        }));
      }
    });
  } else {
    checkTableValueErrorState(
      newValue,
      index,
      values,
      tableValueErrors,
      setTableValueErrors,
      clearCustomErrors
    );
  }

  validateMinusSign(newValue, `value-${index}`, customErrors, setCustomErrors, clearCustomErrors);
  return areUnique;
};

const checkTableValueErrorState = (
  value: string,
  index: number,
  values: AdvancedTagMapping,
  tableValueErrors: number[][],
  setTableValueErrors: React.Dispatch<React.SetStateAction<number[][]>>,
  clearCustomErrors: (name: string, specificErrorValue: string, clearEntireKey?: boolean) => void
) => {
  tableValueErrors.forEach((errorArray) => {
    if (
      errorArray.includes(index) &&
      new Set(
        errorArray.map((errorIndex) => {
          if (errorIndex !== index) {
            return values[`value-${errorIndex}`];
          }
          return value;
        })
      ).size === errorArray.length
    ) {
      errorArray.forEach((errorIndex) => {
        clearCustomErrors(`value-${errorIndex}`, TABLE_VALIDATION_UNIQUE_VALUES_ERROR_MESSAGE);
      });
      setTableValueErrors((prevValue) => prevValue.filter((array) => !isEqual(array, errorArray)));
    } else if (errorArray.includes(index)) {
      const filteredErrorArray = errorArray.filter((errorIndex) => errorIndex !== index);
      const filteredErrorValues = filteredErrorArray.map(
        (filteredIndex) => values[`value-${filteredIndex}`]
      );
      if (!filteredErrorValues.includes(value)) {
        clearCustomErrors(`value-${index}`, TABLE_VALIDATION_UNIQUE_VALUES_ERROR_MESSAGE);
        setTableValueErrors((prevValue) =>
          prevValue.map((arr) => arr.filter((val) => val !== index))
        );
      }
    }
  });
};
